import { DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  map,
  Observable,
  of,
  scan,
  switchMap,
  tap,
} from 'rxjs';
import { IRolesSearchParams, IUsersSearchParams } from '../model';
import {
  SearchUserResponseItem,
  SortDirection,
  UserClient,
} from '../../../core/openapi/bff-admin-api';
import { RoleClient, SearchRolesResponseItem } from '../../../core/openapi/identity';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { outputError } from '../../../shared/utils/output-error.function';
import { LoadingState } from '../../../shared/types/loading.type';
import { IDictionaryItem } from '../../../shared/types/dictionary-item.interface';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../../shared/const/pagination.constants';

@Injectable()
export class UsersService {
  private readonly userBffAdminClient: UserClient = inject(UserClient);
  private readonly roleIdentityClient: RoleClient = inject(RoleClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _usersLoadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.LOADING,
  );
  public readonly usersLoadingState: Signal<LoadingState> = this._usersLoadingState.asReadonly();

  private readonly _rolesLoadingState: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  public readonly rolesLoadingState: Signal<LoadingState> = this._rolesLoadingState.asReadonly();

  public readonly totalUsersCount: WritableSignal<number> = signal<number>(0);
  public readonly totalRolesCount: WritableSignal<number> = signal<number>(0);

  private readonly roleOptionsPageSize = 10 as const;

  private readonly usersRequest$: BehaviorSubject<IUsersSearchParams> =
    new BehaviorSubject<IUsersSearchParams>({ skip: 0, take: DEFAULT_TABLE_PAGE_SIZE });

  private readonly rolesRequest$: BehaviorSubject<IRolesSearchParams> =
    new BehaviorSubject<IRolesSearchParams>({ skip: 0, take: this.roleOptionsPageSize });

  public readonly users$: Observable<SearchUserResponseItem[]> = this.usersRequest$.pipe(
    tap(() => this._usersLoadingState.set(LoadingState.LOADING)),
    switchMap((params) =>
      this.userBffAdminClient
        .v1UserSearchPost({
          skip: params.skip,
          take: params.take,
          keyword: params.keyword,
          roleIds:
            Array.isArray(params.roleIds) && params.roleIds.length > 0 ? params.roleIds : null,
          includeTotalCount: true,
          sortExpressions: [
            { direction: SortDirection.NUMBER_0, propertyName: 'statusId' },
            { direction: SortDirection.NUMBER_1, propertyName: 'createdDate' },
          ],
          statusIds: null,
        })
        .pipe(
          catchError((error) => {
            this.toastService.error({
              message: this.translateService.instant(
                'monitoring.users.toast.users-fetching-failed',
                { error: outputError(error, this.translateService.instant('errors.unknown')) },
              ),
            });
            this.totalUsersCount.set(0);
            return of({ items: [], totalCount: 0 });
          }),
        ),
    ),
    tap((res) => {
      this.totalUsersCount.set(res.totalCount ?? 0);
      this._usersLoadingState.set(LoadingState.READY);
    }),
    map(({ items }) => (items as SearchUserResponseItem[]) ?? []),
    takeUntilDestroyed(this.destroyRef),
  );

  public readonly existingRoles$: Observable<IDictionaryItem<string>[]> = this.rolesRequest$.pipe(
    tap(() => this._rolesLoadingState.set(LoadingState.LOADING)),
    concatMap((params) =>
      this.roleIdentityClient
        .roleSearchPost({
          skip: params.skip,
          take: params.take,
          keyword: params.keyword,
          includeTotalCount: true,
          sortExpressions: [{ propertyName: 'name', direction: SortDirection.NUMBER_1 }],
        })
        .pipe(
          catchError((error) => {
            this.toastService.error({
              message: this.translateService.instant(
                'monitoring.users.toast.roles-fetching-failed',
                { error: outputError(error, this.translateService.instant('errors.unknown')) },
              ),
            });
            this.totalRolesCount.set(0);
            return of({ items: [], totalCount: 0 });
          }),
        ),
    ),
    tap((res) => {
      this.totalRolesCount.set(res.totalCount ?? 0);
      this._rolesLoadingState.set(LoadingState.READY);
    }),
    map(
      ({ items }) =>
        (items as SearchRolesResponseItem[] | null)?.map(
          (role: SearchRolesResponseItem): IDictionaryItem<string> => ({
            id: role.id,
            name: role.name,
          }),
        ) ?? [],
    ),
    scan(
      (
        acc: IDictionaryItem<string>[],
        newbies: IDictionaryItem<string>[],
      ): IDictionaryItem<string>[] => [...acc, ...newbies],
      [] as IDictionaryItem<string>[],
    ),
    takeUntilDestroyed(this.destroyRef),
  );

  public triggerUsersUpdate(params: Partial<IUsersSearchParams>): void {
    this.usersRequest$.next({
      ...this.usersRequest$.value,
      ...params,
    });
  }

  public triggerRolesUpdate(params: Partial<IRolesSearchParams>): void {
    this.rolesRequest$.next({
      ...this.rolesRequest$.value,
      ...params,
    });
  }

  public loadMoreRoles(): void {
    const prevOffset = this.rolesRequest$.value.skip ?? 0;
    const nextOffset = prevOffset + this.roleOptionsPageSize;
    if (this.totalRolesCount() > nextOffset) {
      this.triggerRolesUpdate({ skip: nextOffset });
    }
  }
}
